/**
 * Channel display name (TV1-2349)
 *
 * ONE place that answers "what do we call this channel in the UI?".
 *
 * Three call sites had each written their own answer, and two of them wrote it
 * wrong in the same way:
 *
 *   label: ( ch as { nickname?: string }).nickname ?? ch.connectorId
 *
 * `SystemIntegrationsReadmodel` has no `nickname` field — see
 * `systemsIntegrations.types.ts:204-233`. The `as { nickname?: string }` cast is
 * what let it compile: it asserts a shape the readmodel does not have, so the
 * read is `undefined` on every row and the fallback to `connectorId` fires every
 * time. Users saw `mercadolibre` where the store's name belongs.
 *
 * The cast is the actual defect. Reading the field off the typed readmodel means
 * a future rename of `label` is a compile error here instead of a silent
 * regression to connector ids.
 */

import { SystemIntegrationsReadmodel } from '@bett3r-dev/teselly-domain';

/**
 * Resolution order:
 *   1. `label`           — the name the user gave this integration.
 *   2. `channelUserName` — the account name reported by the channel itself.
 *   3. `connectorId`     — last resort, so the option is never blank.
 *
 * Blank-but-present labels (`''`, `'   '`) fall through rather than rendering an
 * empty option: an integration saved with a whitespace label is a real case and
 * an unlabelled row in a Select is unusable.
 */
export const channelDisplayName = (
  channel: Pick<SystemIntegrationsReadmodel, 'label' | 'channelUserName' | 'connectorId'>
): string => {
  const label = channel.label?.trim();
  if ( label ) return label;

  const userName = channel.channelUserName?.trim();
  if ( userName ) return userName;

  return channel.connectorId;
};
